/**
 * Product Manuals JavaScript
 * 
 * Provides performant PDF manual browsing with:
 * - GraphQL queries to Shopify Storefront API
 * - Lazy loading with pagination
 * - Real-time search and filtering
 * - Download tracking via analytics
 * - Responsive design
 * 
 * @version 1.0.0
 */

class ProductManualsManager {
  constructor() {
    this.storefrontAccessToken = window.Shopify.storefrontAccessToken || '';
    this.shopDomain = window.Shopify.shop || '';
    this.apiVersion = '2025-01';
    this.storefrontApiUrl = `https://${this.shopDomain}/api/${this.apiVersion}/graphql.json`;
    
    this.allManuals = [];
    this.filteredManuals = [];
    this.currentPage = 1;
    this.itemsPerPage = 12;
    this.searchTerm = '';
    this.selectedBrand = '';
    this.selectedCategory = '';
    
    this.init();
  }

  /**
   * Initialize the manager
   */
  init() {
    this.bindElements();
    this.attachEventListeners();
    this.fetchManuals();
  }

  /**
   * Cache DOM elements
   */
  bindElements() {
    this.elements = {
      searchInput: document.getElementById('manual-search'),
      brandFilter: document.getElementById('brand-filter'),
      categoryFilter: document.getElementById('category-filter'),
      manualsGrid: document.getElementById('manuals-grid'),
      loadingState: document.getElementById('loading-state'),
      emptyState: document.getElementById('empty-state'),
      resultsCount: document.getElementById('results-count'),
      pagination: document.getElementById('pagination'),
      prevButton: document.getElementById('prev-page'),
      nextButton: document.getElementById('next-page'),
      pageInfo: document.getElementById('page-info'),
      pdfModal: document.getElementById('pdf-modal')
    };
  }

  /**
   * Attach event listeners
   */
  attachEventListeners() {
    // Search input with debouncing
    let searchTimeout;
    this.elements.searchInput?.addEventListener('input', (e) => {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        this.searchTerm = e.target.value.toLowerCase().trim();
        this.filterAndRender();
      }, 300);
    });

    // Brand filter
    this.elements.brandFilter?.addEventListener('change', (e) => {
      this.selectedBrand = e.target.value;
      this.filterAndRender();
    });

    // Category filter
    this.elements.categoryFilter?.addEventListener('change', (e) => {
      this.selectedCategory = e.target.value;
      this.filterAndRender();
    });

    // Pagination
    this.elements.prevButton?.addEventListener('click', () => this.previousPage());
    this.elements.nextButton?.addEventListener('click', () => this.nextPage());

    // Modal close
    this.elements.pdfModal?.querySelector('.modal-close')?.addEventListener('click', () => {
      this.closeModal();
    });

    this.elements.pdfModal?.querySelector('.modal-overlay')?.addEventListener('click', () => {
      this.closeModal();
    });

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.elements.pdfModal?.style.display !== 'none') {
        this.closeModal();
      }
    });
  }

  /**
   * Fetch manuals from Shopify Storefront API with pagination
   */
  async fetchManuals() {
    try {
      this.showLoading();

      console.log('=== DEBUGGING MANUALS FETCH ===');
      console.log('Shop Domain:', this.shopDomain);
      console.log('API URL:', this.storefrontApiUrl);
      console.log('Access Token:', this.storefrontAccessToken ? 'SET' : 'MISSING');

      // GraphQL query to fetch metaobjects with pagination
      const query = `
        query GetProductManuals($first: Int!, $after: String) {
          metaobjects(type: "product_manual", first: $first, after: $after) {
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              node {
                id
                handle
                fields {
                  key
                  value
                  reference {
                    ... on MediaImage {
                      image {
                        url
                        altText
                      }
                    }
                    ... on GenericFile {
                      url
                      alt
                    }
                  }
                }
              }
            }
          }
        }
      `;

      let allEdges = [];
      let hasNextPage = true;
      let cursor = null;
      let pageCount = 0;

      console.log('Fetching all metaobjects with pagination...');

      // Fetch all pages
      while (hasNextPage) {
        pageCount++;
        console.log(`Fetching page ${pageCount}...`);

        const response = await fetch(this.storefrontApiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this.storefrontAccessToken
          },
          body: JSON.stringify({
            query,
            variables: { first: 250, after: cursor }
          })
        });

        console.log(`Page ${pageCount} Response Status:`, response.status);
        const data = await response.json();

        if (data.errors) {
          console.error('GraphQL Errors:', data.errors);
          throw new Error(data.errors[0].message);
        }

        const metaobjects = data.data.metaobjects;
        allEdges = allEdges.concat(metaobjects.edges);
        
        hasNextPage = metaobjects.pageInfo.hasNextPage;
        cursor = metaobjects.pageInfo.endCursor;
        
        console.log(`Page ${pageCount}: Fetched ${metaobjects.edges.length} manuals. Total so far: ${allEdges.length}`);
      }

      console.log('Total manuals fetched:', allEdges.length);

      this.allManuals = this.parseManuals(allEdges);
      this.filteredManuals = [...this.allManuals];
      
      console.log('Parsed manuals:', this.allManuals.length);
      if (this.allManuals.length > 0) {
        console.log('First manual:', this.allManuals[0]);
      }

      this.renderManuals();
      this.hideLoading();

    } catch (error) {
      console.error('Error fetching manuals:', error);
      this.showError('Failed to load manuals. Please try again later.');
    }
  }

  /**
   * Parse metaobjects into manual objects
   */
  parseManuals(edges) {
    return edges.map(edge => {
      const fields = edge.node.fields;
      const manual = {
        id: edge.node.id,
        handle: edge.node.handle
      };

      fields.forEach(field => {
        if (field.key === 'pdf_file' && field.reference) {
          manual.pdfUrl = field.reference.url;
          manual.alt = field.reference.alt;
        } else {
          manual[field.key] = field.value;
        }
      });

      return manual;
    });
  }

  /**
   * Filter manuals based on search and category
   */
  filterAndRender() {
    this.filteredManuals = this.allManuals.filter(manual => {
      // Brand filter
      if (this.selectedBrand && manual.brand !== this.selectedBrand) {
        return false;
      }

      // Category filter
      if (this.selectedCategory && manual.category !== this.selectedCategory) {
        return false;
      }

      // Search filter
      if (this.searchTerm) {
        const searchableText = [
          manual.title,
          manual.brand,
          manual.model,
          manual.serial_number,
          manual.category,
          manual.tags
        ].join(' ').toLowerCase();

        if (!searchableText.includes(this.searchTerm)) {
          return false;
        }
      }

      return true;
    });

    this.currentPage = 1;
    this.renderManuals();
  }

  /**
   * Render manuals to the grid
   */
  renderManuals() {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    const paginatedManuals = this.filteredManuals.slice(startIndex, endIndex);

    // Update results count
    this.elements.resultsCount.textContent = 
      `${this.filteredManuals.length} manual${this.filteredManuals.length !== 1 ? 's' : ''} found`;

    // Show empty state if no results
    if (this.filteredManuals.length === 0) {
      this.elements.manualsGrid.style.display = 'none';
      this.elements.emptyState.style.display = 'block';
      this.elements.pagination.style.display = 'none';
      return;
    }

    // Hide empty state
    this.elements.emptyState.style.display = 'none';
    this.elements.manualsGrid.style.display = 'grid';

    // Render manual cards
    this.elements.manualsGrid.innerHTML = paginatedManuals
      .map(manual => this.createManualCard(manual))
      .join('');

    // Attach click handlers to cards
    this.attachCardListeners();

    // Update pagination
    this.updatePagination();

    // Lazy load images
    this.lazyLoadImages();
  }

  /**
   * Create HTML for a manual card
   */
  createManualCard(manual) {
    const brandColor = this.getBrandColor(manual.brand);
    const brandLogo = this.getBrandLogo(manual.brand);
    
    return `
      <div class="manual-card" data-manual-id="${manual.id}" data-handle="${manual.handle}">
        <div class="manual-card-layout">
          ${brandLogo ? `
            <div class="manual-brand-logo">
              <img src="${brandLogo}" alt="${manual.brand} logo" loading="lazy" />
            </div>
          ` : ''}
          <div class="manual-card-content">
            <div class="manual-badges">
              <span class="manual-category">
                ${manual.category || 'Uncategorized'}
              </span>
            </div>
            <p class="manual-model"><strong>Model:</strong> ${this.escapeHtml(manual.model || 'N/A')}</p>
            ${manual.serial_number ? `<p class="manual-serial"><strong>Serial:</strong> ${this.escapeHtml(manual.serial_number)}</p>` : ''}
            <div class="manual-meta">
              <a href="${manual.pdfUrl}" 
                 class="manual-download" 
                 download
                 onclick="event.stopPropagation(); trackDownload('${manual.id}', '${manual.title}')">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M8 12L3 7l1.5-1.5L7 8V0h2v8l2.5-2.5L13 7l-5 5zm-7 2h14v2H1v-2z"/>
                </svg>
                Download
              </a>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Get color for brand
   */
  getBrandColor(brand) {
    const colors = {
      'GENIE': '#FF6B00',       // Orange
      'GRADALL': '#0066CC',     // Blue
      'HAULOTTE': '#E31837',    // Red
      'INGERSOL RAND': '#FFD100', // Yellow
      'JLG': '#00A3E0',         // Light Blue
      'LULL': '#006341',        // Green
      'MEC': '#8B0000',         // Dark Red
      'SKYJACK': '#1B75BC',     // Sky Blue
      'SKYTRACK': '#FFA500',    // Gold
      'TEREX': '#ED1C24'        // Bright Red
    };
    return colors[brand] || '#757575';
  }

  /**
   * Get logo URL for brand
   */
  getBrandLogo(brand) {
    const logos = {
      'SKYJACK': 'https://cdn.shopify.com/s/files/1/0837/3682/2071/files/Skyjack_fc2ed146-4ca8-4142-8bdb-04591135b90a.jpg?v=1767136974',
      'GRADALL': 'https://cdn.shopify.com/s/files/1/0837/3682/2071/files/GRadall.jpg?v=1767136973',
      'TEREX': 'https://cdn.shopify.com/s/files/1/0837/3682/2071/files/Terex.jpg?v=1767136974',
      'GENIE': 'https://cdn.shopify.com/s/files/1/0837/3682/2071/files/Genie_d3c98f79-9f92-49a7-b1f4-816c53894f41.jpg?v=1767136974',
      'LULL': 'https://cdn.shopify.com/s/files/1/0837/3682/2071/files/Lull.jpg?v=1767136973',
      'HAULOTTE': 'https://cdn.shopify.com/s/files/1/0837/3682/2071/files/Hauloutte.jpg?v=1767136973',
      'JLG': 'https://cdn.shopify.com/s/files/1/0837/3682/2071/files/JLG_de07a3f4-7bd9-456e-a904-2cc8789a22cf.jpg?v=1767136973',
      'SKYTRACK': 'https://cdn.shopify.com/s/files/1/0837/3682/2071/files/Skytrak.jpg?v=1767136974',
      'MEC': 'https://cdn.shopify.com/s/files/1/0837/3682/2071/files/Mec.jpg?v=1767136974',
      'INGERSOL RAND': 'https://cdn.shopify.com/s/files/1/0837/3682/2071/files/Ingersoll.jpg?v=1767136974'
    };
    return logos[brand] || null;
  }

  /**
   * Attach click listeners to manual cards
   */
  attachCardListeners() {
    document.querySelectorAll('.manual-card').forEach(card => {
      card.addEventListener('click', (e) => {
        if (!e.target.closest('.manual-download')) {
          const manualId = card.dataset.manualId;
          const manual = this.allManuals.find(m => m.id === manualId);
          if (manual) {
            this.showModal(manual);
          }
        }
      });
    });
  }

  /**
   * Show modal with manual details
   */
  showModal(manual) {
    const modal = this.elements.pdfModal;
    const modalTitle = modal.querySelector('#modal-title');
    const modalDetails = modal.querySelector('#modal-details');
    const modalDownload = modal.querySelector('#modal-download');
    const modalView = modal.querySelector('#modal-view');

    modalTitle.textContent = manual.title || manual.model;
    modalDetails.innerHTML = `
      <p><strong>Category:</strong> ${manual.category || 'N/A'}</p>
      <p><strong>Model:</strong> ${manual.model || 'N/A'}</p>
      ${manual.serial_number ? `<p><strong>Serial Number:</strong> ${manual.serial_number}</p>` : ''}
      ${manual.tags ? `<p><strong>Tags:</strong> ${manual.tags}</p>` : ''}
    `;

    modalDownload.href = manual.pdfUrl;
    modalDownload.download = manual.title || 'manual.pdf';
    modalView.href = manual.pdfUrl;

    modal.style.display = 'block';
    document.body.style.overflow = 'hidden';

    // Track modal view
    this.trackEvent('manual_view', {
      manual_id: manual.id,
      manual_title: manual.title
    });
  }

  /**
   * Close modal
   */
  closeModal() {
    this.elements.pdfModal.style.display = 'none';
    document.body.style.overflow = '';
  }

  /**
   * Update pagination controls
   */
  updatePagination() {
    const totalPages = Math.ceil(this.filteredManuals.length / this.itemsPerPage);

    if (totalPages <= 1) {
      this.elements.pagination.style.display = 'none';
      return;
    }

    this.elements.pagination.style.display = 'flex';
    this.elements.pageInfo.textContent = `Page ${this.currentPage} of ${totalPages}`;
    
    this.elements.prevButton.disabled = this.currentPage === 1;
    this.elements.nextButton.disabled = this.currentPage === totalPages;
  }

  /**
   * Navigate to previous page
   */
  previousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.renderManuals();
      this.scrollToTop();
    }
  }

  /**
   * Navigate to next page
   */
  nextPage() {
    const totalPages = Math.ceil(this.filteredManuals.length / this.itemsPerPage);
    if (this.currentPage < totalPages) {
      this.currentPage++;
      this.renderManuals();
      this.scrollToTop();
    }
  }

  /**
   * Scroll to top of manuals section
   */
  scrollToTop() {
    this.elements.manualsGrid.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'start' 
    });
  }

  /**
   * Lazy load images for performance
   */
  lazyLoadImages() {
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            img.src = img.dataset.src;
            imageObserver.unobserve(img);
          }
        });
      });

      document.querySelectorAll('img[data-src]').forEach(img => {
        imageObserver.observe(img);
      });
    }
  }

  /**
   * Show loading state
   */
  showLoading() {
    this.elements.loadingState.style.display = 'block';
    this.elements.manualsGrid.style.display = 'none';
    this.elements.emptyState.style.display = 'none';
  }

  /**
   * Hide loading state
   */
  hideLoading() {
    this.elements.loadingState.style.display = 'none';
  }

  /**
   * Show error message
   */
  showError(message) {
    this.hideLoading();
    this.elements.emptyState.style.display = 'block';
    this.elements.emptyState.innerHTML = `
      <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
        <circle cx="32" cy="32" r="30" stroke="currentColor" stroke-width="2"/>
        <path d="M32 16v20M32 44h.01" stroke="currentColor" stroke-width="2"/>
      </svg>
      <h3>Error Loading Manuals</h3>
      <p>${message}</p>
    `;
  }

  /**
   * Track analytics events
   */
  trackEvent(eventName, properties = {}) {
    // Google Analytics
    if (typeof gtag !== 'undefined') {
      gtag('event', eventName, properties);
    }

    // Shopify Analytics
    if (window.ShopifyAnalytics) {
      window.ShopifyAnalytics.lib.track(eventName, properties);
    }

    console.log('Analytics Event:', eventName, properties);
  }

  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

/**
 * Track download events
 */
function trackDownload(manualId, manualTitle) {
  if (typeof gtag !== 'undefined') {
    gtag('event', 'download', {
      event_category: 'Manual',
      event_label: manualTitle,
      value: manualId
    });
  }

  if (window.ShopifyAnalytics) {
    window.ShopifyAnalytics.lib.track('manual_download', {
      manual_id: manualId,
      manual_title: manualTitle
    });
  }
}

/**
 * Initialize when DOM is ready
 */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ProductManualsManager();
  });
} else {
  new ProductManualsManager();
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ProductManualsManager;
}

